import { Injectable, inject } from '@angular/core';

import { take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { FilterParams, FilterParamsMap, FilterTags } from '@models/filter.model';
import { SeoService } from '@services/seo.service';
import { PricePipe } from '@pipes/price.pipe';

@Injectable({
  providedIn: 'root',
})
export class FilterTagsService {
  private translateService = inject(TranslateService);
  private seoService = inject(SeoService);
  private pricePipe = inject(PricePipe);

  daysInStock(tags: FilterTags[]): FilterTags[] {
    const comingSoonFrom = tags.some((tag: FilterTags) => tag.key === 'daysInStockFrom' && tag.value === 1);
    const comingSoonTo = tags.some((tag: FilterTags) => tag.key === 'daysInStockTo' && tag.value === 30);

    if (comingSoonFrom && comingSoonTo) {
      const combinedTag: FilterTags = { key: 'comingSoon', value: [30], title: '' };
      tags = tags.filter((tag: FilterTags) => tag.key !== 'daysInStockFrom' && tag.key !== 'daysInStockTo');
      tags.push(combinedTag);
    }

    const inStockFrom = tags.some((tag: FilterTags) => tag.key === 'daysInStockFrom' && typeof tag.value === 'number' && tag.value > 30);
    if (inStockFrom) {
      const combinedTag: FilterTags = { key: 'inStock', value: [31], title: '' };
      tags = tags.filter((tag: FilterTags) => tag.key !== 'daysInStockFrom');
      tags.push(combinedTag);
    }

    return tags;
  }

  setTitleAndDescription(title: string): void {
    title = title.trim().length ? title : this.translateService.instant('list.title');
    const seoTitle: string = this.translateService.instant('list.seoTitle', { title: title }).replace(/  +/g, ' ').trim();
    let seoDescription: string = this.translateService.instant('list.seoDescription', { title: title }).replace(/  +/g, ' ').trim();
    seoDescription = seoDescription.trim();
    this.seoService.setTitle(seoTitle);
    this.seoService.setDescription(seoDescription);
    this.seoService.setOgData({ title: seoTitle, description: seoDescription });
  }

  generateTags(data: any, filterParams: FilterParams, filterParamsMap: FilterParamsMap): FilterTags[] {
    let tags: FilterTags[] = [];

    let makeModelTitle: string = '';
    let bodyTypeTitle: string = '';
    let fuelTitle: string = '';
    let yearTitle: string = '';
    let gearboxTitle: string = '';

    for (const key in filterParams) {
      if (key === 'makeModelId') {
        const filterParamsKey = filterParams[key];
        if (filterParamsKey) {
          for (let make of filterParamsKey) {
            const makeData = data.make.find((item: any) => item.id === make.makeId);
            if (makeData) {
              const tag: FilterTags = { key, value: makeData.id, title: makeData.title };
              tags.push(tag);
              if (make.modelId?.length) {
                for (let model of make.modelId) {
                  const modelData = makeData.model.find((item: any) => item.id === model);
                  makeModelTitle += (makeModelTitle.length ? ', ' : '') + makeData.title + ' ' + modelData.title;
                  const tag: FilterTags = { key, subKey: 'model', value: modelData.id, title: modelData.title };
                  tags.push(tag);
                }
              } else {
                makeModelTitle += (makeModelTitle.length ? ', ' : ' ') + makeData.title;
              }
            }
          }
        }
      }

      if (!filterParams.hasOwnProperty(key)) continue;

      const param = filterParams[key as keyof FilterParams];
      const filterItem = filterParamsMap[key];
      if (!filterItem) continue;

      const { key: filterKey, id: filterId, title: filterTitle } = filterItem;
      const processParam = (value: any) => {
        if (data[filterKey]) {
          let item: any;
          if (filterKey !== 'equipment') {
            item = data[filterKey].find((item: any) => item[filterId] === value);
          } else {
            for (const property in data[filterKey]) {
              const itemFind = data[filterKey][property].find((item: any) => item[filterId] === value);
              if (itemFind) item = itemFind;
            }
          }
          if (key === 'priceFrom' || key === 'priceTo') item = { id: value, slug: value, title: this.pricePipe.transform(value) };
          if (key === 'monthlyPaymentFrom' || key === 'monthlyPaymentTo') item = { id: value, slug: value, title: this.pricePipe.transform(value) };
          if (key === 'leasingAdvance') item = { id: value, slug: value, title: this.pricePipe.transform(value) };
          //if (key === 'leasingNumberOfMonth') item = { id: value, slug: value, title: value + ' ' + this.translateService.instant('globals.months.5') };

          if (item) {
            if (key === 'bodyTypeId') {
              bodyTypeTitle += (bodyTypeTitle.length ? ', ' : '') + item[filterTitle];
            } else if (key === 'fuelTypeId') {
              fuelTitle += (fuelTitle.length ? ', ' : '') + item[filterTitle];
            } else if (key === 'yearFrom') {
              yearTitle += (yearTitle.length ? ', ' : '') + item[filterTitle];
            } else if (key === 'gearboxId') {
              gearboxTitle += (gearboxTitle.length ? ', ' : '') + item[filterTitle];
            }
            const tag: FilterTags = { key, value: item[filterId], title: item[filterTitle] };
            tags.push(tag);
          }
        } else {
          const tag: FilterTags = { key, value, title: filterTitle };
          tags.push(tag);

          tags = this.daysInStock(tags);
        }
      };

      if (Array.isArray(param)) {
        param.forEach(processParam);
      } else {
        processParam(param);
      }
    }

    let title: string = (makeModelTitle ? makeModelTitle + ' ' : '') + (bodyTypeTitle ? bodyTypeTitle + ' ' : '') + (fuelTitle ? fuelTitle + ' ' : '') + (yearTitle ? yearTitle + ' ' : '') + (gearboxTitle ? gearboxTitle + ' ' : '');
    this.setTitleAndDescription(title);
    this.translateService.onLangChange.pipe(take(1)).subscribe(() => {
      this.setTitleAndDescription(title);
    });
    return tags;
  }
}
